import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { topics } from '@/data/videoTopics';
import { ArrowLeft, Video, Search, Trash2, Database, FileCode, Plus } from 'lucide-react';

interface DatabaseVideo {
  id: string;
  category_id: string;
  title: string;
  embed_code: string;
  description: string | null;
  created_at: string;
}

const AdminVideos = () => {
  const [databaseVideos, setDatabaseVideos] = useState<DatabaseVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDatabaseVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (videoId: string) => {
    if (!confirm('Are you sure you want to delete this video?')) return;

    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId);

      if (error) throw error;

      toast.success('Video deleted successfully');
      fetchVideos();
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.error(error.message || 'Failed to delete video');
    }
  };

  const getCategoryTitle = (catId: string) => {
    const category = topics.find(t => t.id === catId);
    return category?.title || catId;
  };

  // Flatten hardcoded videos for display
  const hardcodedVideos = topics.flatMap(topic => 
    topic.videos.map(video => ({
      ...video,
      categoryId: topic.id,
      categoryTitle: topic.title,
    }))
  );

  const filteredDatabaseVideos = databaseVideos.filter(video => {
    const search = searchTerm.toLowerCase();
    return (
      video.title.toLowerCase().includes(search) ||
      getCategoryTitle(video.category_id).toLowerCase().includes(search)
    );
  });

  const filteredHardcodedVideos = hardcodedVideos.filter(video => {
    const search = searchTerm.toLowerCase();
    return (
      video.title.toLowerCase().includes(search) ||
      video.categoryTitle.toLowerCase().includes(search)
    );
  });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 pt-24">
        <div className="mb-6">
          <Button variant="ghost" asChild className="mb-4">
            <Link to="/admin">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-playfair font-bold text-foreground">Manage Videos</h1>
              <p className="text-muted-foreground">View and manage all platform videos</p>
            </div>
            <Button asChild>
              <Link to="/admin/upload-video">
                <Plus className="mr-2 h-4 w-4" />
                Upload Video
              </Link>
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <Tabs defaultValue="uploaded" className="space-y-4">
          <TabsList>
            <TabsTrigger value="uploaded" className="gap-2">
              <Database className="h-4 w-4" />
              Uploaded ({databaseVideos.length})
            </TabsTrigger>
            <TabsTrigger value="library" className="gap-2">
              <FileCode className="h-4 w-4" />
              Library ({hardcodedVideos.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="uploaded">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Uploaded Videos
                </CardTitle>
                <CardDescription>
                  Videos uploaded through the admin dashboard
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-muted-foreground text-center py-8">Loading videos...</p>
                ) : filteredDatabaseVideos.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    {searchTerm ? 'No videos match your search' : 'No uploaded videos yet'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Date Added</TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredDatabaseVideos.map((video) => (
                          <TableRow key={video.id}>
                            <TableCell className="font-medium max-w-[300px] truncate">
                              {video.title}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {getCategoryTitle(video.category_id)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {new Date(video.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(video.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="library">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCode className="h-5 w-5" />
                  Library Videos
                </CardTitle>
                <CardDescription>
                  Videos embedded in the codebase (read-only)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredHardcodedVideos.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No videos match your search
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Title</TableHead>
                          <TableHead>Category</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredHardcodedVideos.slice(0, 50).map((video, index) => (
                          <TableRow key={`${video.categoryId}-${video.id}-${index}`}>
                            <TableCell className="font-medium max-w-[300px] truncate">
                              {video.title}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {video.categoryTitle}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredHardcodedVideos.length > 50 && (
                      <p className="text-center text-muted-foreground py-4">
                        Showing 50 of {filteredHardcodedVideos.length} videos
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default AdminVideos;
